import React, { useState } from 'react';
import { Button, Modal } from '../ui';

interface RemoveSessionModalProps {
  isOpen: boolean;
  sessionDateLabel: string;
  lessonName: string;
  isLoading?: boolean;
  stackLevel?: 'default' | 'elevated';
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}

export function RemoveSessionModal({
  isOpen,
  sessionDateLabel,
  lessonName,
  isLoading = false,
  stackLevel = 'default',
  onCancel,
  onConfirm,
}: RemoveSessionModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError('');
    onCancel();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Remove this session" maxWidth="sm" stackLevel={stackLevel}>
      <div className="space-y-4 mb-6">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <strong className="text-gray-900 dark:text-gray-100">{lessonName}</strong>
          {' · '}
          {sessionDateLabel}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          This session has no confirmed enrollments. It will be removed from the schedule and will not
          appear as cancelled.
        </p>
        {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
      </div>
      <div className="flex gap-3">
        <Button variant="outline" onClick={handleClose} disabled={submitting || isLoading}>
          Close
        </Button>
        <Button
          variant="destructive"
          onClick={() => void handleSubmit()}
          disabled={submitting || isLoading}
          isLoading={submitting || isLoading}
        >
          Remove session
        </Button>
      </div>
    </Modal>
  );
}

export default RemoveSessionModal;
